// SocketContext.js
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import io from 'socket.io-client';

// Get API base URL from environment variable
const API_URL = process.env.REACT_APP_API_ROOT || 'http://localhost:4000';

// Creating the WebSocket Context
const SocketContext = createContext(null);

// Custom hook to use the socket and its status
export const useSocket = () => useContext(SocketContext);

// Connection states
export const CONNECTION_STATES = {
    CONNECTED: 'connected',
    CONNECTING: 'connecting',
    RECONNECTING: 'reconnecting', // A3: surfaced during socket.io reconnect attempts
    DISCONNECTED: 'disconnected',
    ERROR: 'error'
};

// A3: bounded reconnect policy.
//   - reconnectionAttempts: hard cap so a permanently-broken server doesn't
//     have the UI spinning forever.
//   - reconnectionDelay / reconnectionDelayMax: exponential backoff bounds.
//   - randomizationFactor: small jitter to spread reconnect storms.
// Values chosen to fit the typical participant network glitch (brief WiFi
// drop, < 30s total) without surviving an outage long enough to skew block
// timing.
export const RECONNECT_POLICY = Object.freeze({
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    randomizationFactor: 0.5,
});

export const SocketProvider = ({ roomID, userID, blockNo=0, children }) => {
    const [socket, setSocket] = useState(null);
    const [connectionState, setConnectionState] = useState(CONNECTION_STATES.DISCONNECTED);
    const [error, setError] = useState(null);
    const [reconnectAttempt, setReconnectAttempt] = useState(0);

    const createSocket = useCallback(() => {
        if (!roomID) {
            setConnectionState(CONNECTION_STATES.ERROR);
            setError(new Error("Cannot create socket: roomID is missing or invalid"));
            return null;
        }

        console.log(`Attempting to connect to socket for room ${roomID}`);

        const newSocket = io(API_URL, {
            transports: ['websocket'],
            query: {
                room_id: roomID,
                type: "User",
                user_id: userID,
                blockNo: blockNo
            },
            ...RECONNECT_POLICY,
            timeout: 10000
        });

        // Set up event listeners
        newSocket.on('connect', () => {
            console.log('Socket connected successfully');
            setConnectionState(CONNECTION_STATES.CONNECTED);
            setError(null);
            setReconnectAttempt(0);
        });

        newSocket.on('connect_error', (err) => {
            console.error('Socket connection error:', err);
            setConnectionState(CONNECTION_STATES.DISCONNECTED);
            setError(err);
        });

        newSocket.on('disconnect', (reason) => {
            console.log(`Socket disconnected. Reason: ${reason}`);
            setConnectionState(CONNECTION_STATES.DISCONNECTED);
        });

        newSocket.on('error', (err) => {
            console.error('Socket error:', err);
            setConnectionState(CONNECTION_STATES.ERROR);
            setError(err);
        });

        // A3: bounded-reconnect surface. socket.io fires these on the
        // client; we surface UI state AND forward to the server so the
        // session document has the same history.
        newSocket.io.on('reconnect_attempt', (attempt) => {
            console.log(`Socket reconnect_attempt ${attempt}/${RECONNECT_POLICY.reconnectionAttempts}`);
            setConnectionState(CONNECTION_STATES.RECONNECTING);
            setReconnectAttempt(attempt);
            try { newSocket.emit('socket_reconnect_attempt', { attempt }); } catch (_e) { /* socket may be mid-handshake */ }
        });

        newSocket.io.on('reconnect', (attempt) => {
            console.log(`Socket reconnected after ${attempt} attempt(s)`);
            setConnectionState(CONNECTION_STATES.CONNECTED);
            setReconnectAttempt(0);
            try { newSocket.emit('socket_reconnected', { attempt }); } catch (_e) { /* noop */ }
        });

        newSocket.io.on('reconnect_failed', () => {
            console.error('Socket reconnect_failed (gave up after bounded attempts)');
            setConnectionState(CONNECTION_STATES.ERROR);
            setError(new Error('Socket reconnect_failed'));
            try { newSocket.emit('socket_reconnect_failed', { attempt: reconnectAttempt, reason: 'bounded_attempts_exhausted' }); } catch (_e) { /* noop */ }
        });

        return newSocket;
    }, [roomID, userID, blockNo, reconnectAttempt]);

    // Effect to create socket when roomID, userID, or blockNo changes
    useEffect(() => {
        const newSocket = createSocket();
        if (newSocket) {
            setSocket(newSocket);
        }

        return () => {
            if (newSocket) {
                console.log("Cleaning up socket connection");
                newSocket.off('connect');
                newSocket.off('connect_error');
                newSocket.off('disconnect');
                newSocket.off('error');
                // A3: also detach manager-level reconnect listeners.
                try {
                    newSocket.io.off('reconnect_attempt');
                    newSocket.io.off('reconnect');
                    newSocket.io.off('reconnect_failed');
                } catch (_e) { /* socket.io versions differ; best-effort */ }
                newSocket.close();
            }
        };
    }, [roomID, userID, blockNo, createSocket]);

    // Provide both the socket and its status to consumers
    const contextValue = {
        socket,
        userID,
        roomID,
        connectionState,
        error,
        // A3: surfaced for UI components that want to display reconnect progress.
        reconnectAttempt,
        maxReconnectAttempts: RECONNECT_POLICY.reconnectionAttempts,
        isConnected: connectionState === CONNECTION_STATES.CONNECTED,
        isReconnecting: connectionState === CONNECTION_STATES.RECONNECTING,
    };

    return (
        <SocketContext.Provider value={contextValue}>
            {children}
        </SocketContext.Provider>
    );
};
